<template>
	<div class="box invoice-supplier-suggestion">
		<h3>{{ $t('invoice.supplier_unknown_title') }}</h3>

		<p v-if="origin" class="invoice-supplier-suggestion__label">
			{{ origin_label }} · {{ $t(origin.eu ? 'invoice.supplier_unknown_origin_eu' : 'invoice.supplier_unknown_origin_out') }}
		</p>

		<div class="form-group">
			<label>{{ $t('invoice.supplier_unknown_id_type') }}</label>
			<select v-model="id_type" class="custom-select mb-2" @change="onIdTypeChange">
				<option value="siren">{{ $t('invoice.supplier_unknown_id_siren') }}</option>
				<option value="vat">{{ $t('invoice.supplier_unknown_id_vat') }}</option>
			</select>

			<p class="invoice-supplier-suggestion__hint">{{ $t(id_type === 'siren' ? 'invoice.supplier_unknown_identifier_hint' : 'invoice.supplier_unknown_vat_hint') }}</p>

			<div class="input-group">
				<input type="text" class="form-control" v-model="identifier" @keyup.enter="search">
				<div class="input-group-append">
					<button class="btn btn-secondary" :disabled="searching" @click="search">
						<font-awesome-icon v-if="searching" :icon="['fal', 'spinner']" spin />
						<font-awesome-icon v-else :icon="['fal', 'search']" />
					</button>
				</div>
			</div>
		</div>

		<template v-if="id_type === 'siren'">
			<div v-if="searched && !pappers" class="alert alert-warning">
				{{ $t('invoice.supplier_unknown_no_result') }}
			</div>

			<div v-if="pappers" class="invoice-supplier-suggestion__card">
				<div class="invoice-supplier-suggestion__label">{{ $t('invoice.supplier_unknown_pappers') }}</div>
				<div class="font-weight-bold">{{ pappers.tiers_rs }}</div>
				<div>{{ pappers.tiers_address1 }}</div>
				<div v-if="pappers.tiers_address2">{{ pappers.tiers_address2 }}</div>
				<div>{{ pappers.tiers_postalcode }} {{ pappers.tiers_town }}</div>
			</div>
		</template>

		<template v-else-if="searched">
			<div v-if="!vat_country_eu" class="alert alert-warning">
				{{ $t('invoice.supplier_unknown_no_vies') }}
			</div>
			<div v-else-if="vies.status == 1" class="invoice-supplier-suggestion__card">
				<div class="invoice-supplier-suggestion__label">{{ $t('invoice.supplier_unknown_vies_valid') }}</div>
				<template v-if="vies.name || vies.address">
					<div class="font-weight-bold">{{ vies.name }}</div>
					<div class="invoice-supplier-suggestion__address">{{ vies.address }}</div>
					<div class="invoice-supplier-suggestion__hint mt-2">{{ $t('invoice.supplier_unknown_vies_compare') }}</div>
				</template>
				<div v-else class="invoice-supplier-suggestion__hint">{{ $t('invoice.supplier_unknown_vies_anonymous') }}</div>
			</div>
			<div v-else-if="vies.status == 2" class="alert alert-warning">
				{{ $t('invoice.supplier_unknown_vies_invalid') }}
			</div>
			<div v-else class="alert alert-info">
				{{ $t('invoice.supplier_unknown_vies_unavailable') }}
			</div>
		</template>

		<div v-if="similar_tiers" class="alert alert-warning mt-3">
			{{ $t('invoice.supplier_unknown_similar', { tiers: similar_tiers.tiers_rs, town: similar_tiers.tiers_town || '' }) }}
			<button class="btn btn-secondary btn-sm btn-block mt-2" :disabled="processing" @click="linkSimilar">
				{{ $t('invoice.supplier_unknown_similar_link') }}
			</button>
		</div>

		<button v-if="pappers" class="btn btn-primary btn-block mt-3" :disabled="processing" @click="createTiers">
			{{ $t('invoice.supplier_unknown_create') }}
			<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
		</button>
		<button v-else-if="!searching" class="btn btn-primary btn-block mt-3" :disabled="processing" @click="createFromInvoice">
			{{ $t('invoice.supplier_unknown_create_from_invoice') }}
		</button>

		<div class="invoice-supplier-suggestion__separator">{{ $t('global.ou') }}</div>

		<p>{{ $t('invoice.supplier_unknown_existing_hint') }}</p>

		<e-select
			v-model="selected_tiers"
			track-by="tiers_id"
			label="tiers_rs"
			:placeholder="$t('tiers.rechercher')"
			:options="tiers"
			:loading="loading_tiers"
			:searchable="true"
			:allow-empty="true"
			:show-labels="false"
		>
			<template slot="noOptions">{{ $t('global.list_empty') }}</template>
		</e-select>

		<button class="btn btn-primary btn-block mt-3" :disabled="!selected_tiers || processing" @click="linkExisting">
			{{ $t('invoice.supplier_unknown_link') }}
			<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" spin />
		</button>
	</div>
</template>


<script type="text/javascript">
	import Vue from 'vue'
	import Countries from '@/assets/lang/countries/countries'
	import Invoice from "@/mixins/Invoice.js"
	import Tiers from "@/mixins/Tiers.js"
	import Shutter from "@/mixins/Shutter.js"
	import ShutterInvoice from "@/mixins/shutters-manager/Invoice.js"

	// Formes juridiques ignorées pour comparer deux raisons sociales
	const LEGAL_FORMS = ['sa', 'sas', 'sasu', 'sarl', 'eurl', 'earl', 'scea', 'sci', 'snc', 'srl', 'sprl', 'bv', 'nv', 'gmbh', 'ag', 'kg', 'ltd', 'limited', 'llc', 'inc', 'plc', 'sl', 'spa']

	export default {
		name: "InvoiceSupplierSupplierSuggestion",
		mixins: [Invoice, Tiers, Shutter, ShutterInvoice],
		props: {
			invoice: { type: Object, default: () => ({}) }
		},

		data() {
			return {
				// Fiche pré-remplie depuis la facture et pays déduit de l'émetteur
				info: null,
				origin: null,
				// siren : annuaire Pappers ; vat : n° de TVA, vérifié par VIES pour un émetteur de l'UE
				id_type: 'siren',
				identifier: '',
				pappers: null,
				vies: null,
				searching: false,
				searched: false,
				processing: false,
				loading_tiers: false,
				tiers: [],
				selected_tiers: null
			}
		},

		computed: {
			origin_label() {
				return Countries[Vue.i18n.locale()].label[this.origin.id]
			},

			normalized_identifier() {
				return this.normalizeVatNumber(this.identifier)
			},

			// Pays désigné par le préfixe du n° de TVA saisi, celui auquel VIES le rattache
			vat_country() {
				return this.countryIdFromIso(this.normalized_identifier.substr(0, 2))
			},

			vat_country_eu() {
				return this.vat_country !== null && this.isEuCountry(this.vat_country)
			},

			/**
			 * Tiers existant qui pourrait être l'émetteur : même n° de TVA, ou raison sociale identique
			 * une fois débarrassée de sa forme juridique. Signalé avant toute création pour éviter un
			 * doublon.
			 *
			 * @return {Object|null}
			 */
			similar_tiers() {
				if(!this.info) {
					return null
				}

				const vat = this.id_type === 'vat' ? this.normalized_identifier : this.info.tiers.tiers_vat_number
				const rs = this.rsKey(this.info.tiers.tiers_rs)

				return this.tiers.find(tiers => (vat && this.normalizeVatNumber(tiers.tiers_vat_number || '') === vat) || (rs && this.rsKey(tiers.tiers_rs) === rs)) || null
			}
		},

		mounted() {
			this.init_component()
		},

		methods: {
			/**
			 * Déduit de la facture la fiche pré-remplie et le pays de l'émetteur, choisit le type
			 * d'identifiant (n° de TVA pour un émetteur étranger, SIREN sinon), charge les tiers et lance
			 * la recherche quand la facture porte un identifiant.
			 */
			async init_component() {
				this.info = this.supplierInfoFromInvoice(this.invoice)
				this.origin = this.info.country

				// Un émetteur étranger s'identifie par son n° de TVA : Pappers ne connaît que la France
				this.id_type = this.origin && this.origin.iso !== 'FR' ? 'vat' : 'siren'
				this.identifier = this.id_type === 'vat' ? this.info.tiers.tiers_vat_number : this.detectedIdentifier()

				this.loading_tiers = true
				this.tiers = await this.loadTiers()
				this.loading_tiers = false

				// L'interrogation part d'elle-même quand la facture porte un identifiant : c'est le cas
				// courant, et l'opérateur n'a alors qu'à valider ce qui lui est proposé.
				if(this.identifier) {
					this.search()
				}
			},

			/**
			 * Identifiant de l'émetteur tel que le document le porte : le SIREN relevé à l'import d'une
			 * facture électronique, ou l'immatriculation reconnue par l'OCR sur un document scanné.
			 *
			 * @return {String}
			 */
			detectedIdentifier() {
				if(this.invoice.suppliersocr_seller_siren) {
					return this.invoice.suppliersocr_seller_siren
				}

				// Le SIRET désigne l'établissement qui facture : plus précis que le SIREN, il est
				// préféré quand l'OCR l'a reconnu.
				return this.info.tiers.tiers_siret || this.info.tiers.tiers_siren
			},

			onIdTypeChange() {
				this.identifier = this.id_type === 'vat' ? this.info.tiers.tiers_vat_number : this.detectedIdentifier()
				this.pappers = null
				this.vies = null
				this.searched = false

				if(this.identifier) {
					this.search()
				}
			},

			/**
			 * Interroge l'annuaire adapté au type d'identifiant : Pappers pour un SIREN / SIRET, VIES pour
			 * le n° de TVA d'un État membre. Hors UE, rien n'est interrogé : la fiche reprendra la facture.
			 */
			async search() {
				if(!this.identifier) {
					return
				}

				this.searching = true
				this.pappers = null
				this.vies = null

				if(this.id_type === 'siren') {
					this.pappers = await this.searchPappersEntreprise(this.identifier)
				}
				// Hors UE, aucun annuaire n'est interrogé : la fiche reprendra la facture
				else if(this.vat_country_eu) {
					this.vies = await this.checkVatDetailsOnline(this.identifier, this.vat_country)
				}

				this.searched = true
				this.searching = false
			},

			/**
			 * Crée le tiers depuis l'annuaire puis lui rattache la facture.
			 */
			async createTiers() {
				this.processing = true

				try {
					const tiers_id = await this.createTiersFromPappers(this.pappers)

					this.processing = false
					await this.onTiersCreated(tiers_id)
				}
				catch(error) {
					this.processing = false
					console.error("SupplierSuggestion::createTiers => ERROR", error)
					this.failureToast(typeof error === 'string' ? error : 'toast.info_save_failed')
				}
			},

			/**
			 * Ouvre la création du tiers pré-remplie de ce que la facture déclare, quand aucun annuaire
			 * ne fournit l'identité de l'émetteur. Le nom et l'adresse VIES ne servent qu'à comparer :
			 * c'est la facture qui fait foi.
			 */
			createFromInvoice() {
				const tiers = { ...this.info.tiers, sources: { ...this.info.sources }, uncertain: this.info.uncertain }

				if(this.id_type === 'vat') {
					tiers.tiers_vat_number = this.normalized_identifier
					tiers.sources.tiers_vat_number = this.vies && this.vies.status == 1 ? 'vies' : 'facture'
				}

				this.setupAddTier(tiers, this.onTiersCreated)
			},

			async onTiersCreated(tiers_id) {
				this.tiers = await this.loadTiers()
				this.selected_tiers = this.tiers.find(tiers => tiers.tiers_id === tiers_id)

				if(!this.selected_tiers) {
					this.failureToast('toast.info_save_failed')
					return
				}

				await this.linkExisting()
			},

			linkSimilar() {
				this.selected_tiers = this.similar_tiers
				this.linkExisting()
			},

			/**
			 * Rattache la facture au tiers désigné et mémorise l'identifiant de l'émetteur : les
			 * factures suivantes du même fournisseur se rangeront seules derrière ce tiers, qu'il
			 * s'agisse du fournisseur lui-même ou d'un tiers de rassemblement.
			 */
			async linkExisting() {
				this.processing = true

				const by_siren = this.id_type === 'siren'
				const result = await this.linkInvoiceTiers(this.invoice.suppliersocr_id, {
					tiers_id: this.selected_tiers.tiers_id,
					siren: this.pappers ? this.pappers.tiers_siren : (by_siren ? this.identifier : null),
					siret: this.pappers ? this.pappers.tiers_siret : null,
					label: this.pappers ? this.pappers.tiers_rs : this.info.tiers.tiers_rs,
					vat_number: by_siren ? this.info.tiers.tiers_vat_number : this.normalized_identifier
				})

				this.processing = false

				if(!result) {
					return
				}

				this.successToast('toast.info_modif_succes')

				if(result.propagated_tiers_invoices > 0) {
					this.infoToast('invoice.supplier_tiers_propagated', { nb: result.propagated_tiers_invoices })
				}

				this.$emit('linked', this.selected_tiers.tiers_id)
			},

			/**
			 * Raison sociale réduite à ses mots, sans accents, ponctuation ni forme juridique, dans un
			 * ordre fixe : « TROIS CHENES (HARAS DES) » et « Haras des Trois Chênes SRL » s'y rejoignent.
			 *
			 * @param {String} rs
			 * @return {String}
			 */
			rsKey(rs) {
				return this.normalizeLabel(rs || '')
					.split(/[^a-z0-9]+/)
					.filter(word => word && !LEGAL_FORMS.includes(word))
					.sort()
					.join(' ')
			}
		}
	}
</script>
